/**
 * Book Page Component
 *
 * Class enrollment booking page with form and helpful information sidebar
 * Following PRP specification lines 719-726
 * BRUTALIST STREETWEAR DESIGN SYSTEM
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { BookingForm } from '../../components/cpr/BookingForm';
import { Card } from '../../components/Card';

export const Book = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Book CPR Training | Schedule Your Certification Class</title>
        <meta name="title" content="Book CPR Training | Schedule Your Certification Class" />
        <meta
          name="description"
          content="Schedule your CPR certification class in Orlando or Atlanta. Choose your course, date, and location. Same-day certification available. Book online today!"
        />
        <meta name="keywords" content="book CPR class, schedule CPR training, CPR appointment, certification booking" />
        <meta name="author" content="CPR Training Direct" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cprtrainingdirect.com/book" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cprtrainingdirect.com/book" />
        <meta property="og:title" content="Book CPR Training | Schedule Your Certification Class" />
        <meta
          property="og:description"
          content="Schedule your CPR certification class in Orlando or Atlanta. Choose your course, date, and location. Same-day certification available. Book online today!"
        />
        <meta property="og:image" content="https://cprtrainingdirect.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content="CPR Training Direct" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://cprtrainingdirect.com/book" />
        <meta name="twitter:title" content="Book CPR Training | Schedule Your Certification Class" />
        <meta
          name="twitter:description"
          content="Schedule your CPR certification class in Orlando or Atlanta. Choose your course, date, and location. Same-day certification available. Book online today!"
        />
        <meta name="twitter:image" content="https://cprtrainingdirect.com/og-image.jpg" />

        {/* Geo Tags for Local SEO */}
        <meta name="geo.region" content="US-FL" />
        <meta name="geo.placename" content="Orlando" />
        <meta name="geo.position" content="28.5383;-81.3792" />
        <meta name="ICBM" content="28.5383, -81.3792" />
      </Helmet>

      <div className="min-h-screen bg-white py-16 md:py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-16">
            <h1
              data-aos="fade-up"
              className="font-display text-6xl md:text-7xl lg:text-8xl font-bold uppercase tracking-wider text-indigo_dye-500 mb-6"
            >
              Book Your Training
            </h1>
            <p
              data-aos="fade-up"
              data-aos-delay="100"
              className="font-body text-lg md:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed"
            >
              Ready to get certified? Fill out the form below and we'll contact you within 24 hours to confirm your class enrollment.
            </p>
          </div>

          {/* Two-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form Column (2/3 width on desktop) */}
            <div data-aos="fade-up" data-aos-delay="200" className="lg:col-span-2">
              <BookingForm />
            </div>

            {/* Sidebar Column (1/3 width on desktop) */}
            <div className="lg:col-span-1 space-y-8">
              {/* What Happens Next */}
              <div data-aos="fade-left" data-aos-delay="300">
              <Card variant="brutalist" title="WHAT HAPPENS NEXT?">
                <ol className="space-y-6">
                  <li className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-12 h-12 bg-amaranth_purple-500 text-white border-2 border-amaranth_purple-700 flex items-center justify-center font-mono font-bold text-xl">
                      1
                    </span>
                    <div>
                      <h4 className="font-mono font-bold uppercase tracking-wide text-gray-900 mb-1">Submit Request</h4>
                      <p className="font-body text-sm text-gray-700 leading-relaxed">
                        Complete the enrollment form with your details and preferences
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-12 h-12 bg-amaranth_purple-500 text-white border-2 border-amaranth_purple-700 flex items-center justify-center font-mono font-bold text-xl">
                      2
                    </span>
                    <div>
                      <h4 className="font-mono font-bold uppercase tracking-wide text-gray-900 mb-1">We'll Contact You</h4>
                      <p className="font-body text-sm text-gray-700 leading-relaxed">
                        Our team will reach out within 24 hours to confirm availability
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-12 h-12 bg-amaranth_purple-500 text-white border-2 border-amaranth_purple-700 flex items-center justify-center font-mono font-bold text-xl">
                      3
                    </span>
                    <div>
                      <h4 className="font-mono font-bold uppercase tracking-wide text-gray-900 mb-1">Confirm & Pay</h4>
                      <p className="font-body text-sm text-gray-700 leading-relaxed">
                        Receive payment instructions and class details via email
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-12 h-12 bg-amaranth_purple-500 text-white border-2 border-amaranth_purple-700 flex items-center justify-center font-mono font-bold text-xl">
                      4
                    </span>
                    <div>
                      <h4 className="font-mono font-bold uppercase tracking-wide text-gray-900 mb-1">Attend Class</h4>
                      <p className="font-body text-sm text-gray-700 leading-relaxed">
                        Show up prepared and get certified the same day
                      </p>
                    </div>
                  </li>
                </ol>
              </Card>
              </div>

              {/* Have Questions */}
              <div data-aos="fade-left" data-aos-delay="400">
              <Card variant="brutalist" title="HAVE QUESTIONS?" className="bg-amaranth_purple-50">
                <p className="font-body text-gray-700 mb-6 leading-relaxed">
                  Our team is here to help! Contact us directly for immediate assistance.
                </p>

                <div className="space-y-4">

                  {/* Contact Page Link */}
                  <div className="pt-4 border-t-4 border-amaranth_purple-200">
                    <Link
                      to="/contact"
                      className="inline-block font-mono text-amaranth_purple-600 hover:text-amaranth_purple-700 font-bold uppercase tracking-wide hover:underline"
                    >
                      Visit our Contact Page →
                    </Link>
                  </div>
                </div>
              </Card>
              </div>

              {/* Quick Info Box */}
              <div data-aos="fade-left" data-aos-delay="500">
              <Card variant="brutalist" title="QUICK INFO">
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-amaranth_purple-500 text-xl">✓</span>
                    <span className="font-body text-gray-700">Same-day certification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amaranth_purple-500 text-xl">✓</span>
                    <span className="font-body text-gray-700">AHA certified instructors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amaranth_purple-500 text-xl">✓</span>
                    <span className="font-body text-gray-700">Flexible scheduling options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amaranth_purple-500 text-xl">✓</span>
                    <span className="font-body text-gray-700">Group discounts available</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amaranth_purple-500 text-xl">✓</span>
                    <span className="font-body text-gray-700">On-site training for groups</span>
                  </li>
                </ul>
              </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Book;
